<template>
    <div>
        <div class="bg-white mb-4 relative">
            <div v-if="item.document === null && !linkOnly">
                <button
                    v-if="!isMainMenu && item.children.length === 0 && !item.name"
                    type="button"
                    class="mb-2 text-xs"
                    @click="$emit('add-parent', index)"
                >
                    Add Document
                </button>
                <div
                    v-show="!isMainMenu && !item.document && !item.name"
                    class="border-b w-full h-px my-3 text-center"
                >
                    <span
                        class="bg-white text-gray-300 text-center inline-block transform -translate-y-1/2"
                        >OR</span
                    >
                </div>
                <input
                    type="text"
                    v-model="item.name"
                    class="h-full max-w-full rounded py-4 block border-b border-gray-200 focus:outline-none focus:border-red-400 transition duration-300 ease-in-out"
                    ref="nameInput"
                    placeholder="Enter a name"
                />
            </div>
            <div v-if="!isMainMenu && item.document === null && linkOnly">
                <button
                    type="button"
                    class="mb-2 text-xs"
                    @click="$emit('add-parent', index)"
                >
                    Add Document
                </button>
            </div>
            <div
                v-if="item.document !== null"
                class="flex flex-col align-items-start pb-1"
            >
                <div
                    class="p-3 border bg-white hover:bg-gray-100 rounded-lg cursor-pointer text-sm relative group"
                >
                    <menu-sub-item
                        @edited="updateName"
                        :name="item.name"
                        :deletable="item.children.length === 0"
                        :child="item.document"
                        :index="index"
                        :sub-index="0"
                        @remove-sub-item="() => $emit('remove-parent')"
                    ></menu-sub-item>
                </div>
                <button
                    v-if="item.children.length === 0"
                    class="mt-1 text-xs hover:text-gray-500"
                    type="button"
                    @click="$emit('change-parent')"
                >
                    Change Document
                </button>
            </div>

            <div v-if="!isMainMenu"
                class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            >
                <reference
                    linkText="No Linked Document"
                    :disabled="true"
                    v-model="item.document"
                    model-data="Menu Document"
                    :creatable="false"
                    :deletable="false"
                    :editable="false"
                />
            </div>
        </div>
        <ul class="space-y-1 mb-6" v-if="hasChildren">
            <draggable
                v-model="item.children"
                @choose="froalaStore.disableFroala = true"
                @unchoose="froalaStore.disableFroala = false"
            >
                <li
                    v-for="(child, subIndex) in item.children"
                    :key="`menu-sub-item-${subIndex}`"
                    class="p-2 bg-white hover:bg-gray-100 rounded-lg cursor-pointer text-sm relative group"
                >
                    <menu-sub-item
                        :child="child"
                        :index="index"
                        :sub-index="subIndex"
                        @edited="(name) => updateSubItemName(name, subIndex)"
                        @remove-sub-item="
                            (payload) => $emit('remove-sub-item', payload)
                        "
                    >
                    </menu-sub-item>
                </li>
            </draggable>
        </ul>

        <button
            v-if="(!!item.document || !!item.name) && hasChildren && item.children.length < limit"
            type="button"
            class="mb-2 text-xs"
            @click="$emit('add-document', index)"
        >
            Add Document
        </button>
        <div v-if="hasIcon">
            <media-reference
                v-model="item.icon"
                label="* Icon"
                :media-types="['Icon']"
            />
        </div>
        <div
            v-if="deletable"
            class="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
            @click="$emit('remove-item', index)"
        >
            <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                ></path>
            </svg>
        </div>
        <error class="text-sm" :error="error"></error>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import EventBus from "../../eventBus";
import MenuSubItem from "./MenuSubItem";
import siteLanguage from '../../config/siteLanguage'
import {useFroalaStore} from "../../stores/froala";
export default {
    name: "MenuItem",
    components: { draggable, MenuSubItem },
    props: {
        item: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        hasChildren: {
            type: Boolean,
            default: true,
        },
        isMainMenu: {
            type: Boolean,
            default: false,
        },
        hasIcon: {
            type: Boolean,
            default: false,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH, id: siteLanguage.language.ENGLISH };
            },
        },
        error: {
            type: String,
            required: false,
        },
        deletable: {
            type: Boolean,
            default: true,
            required: false,
        },
        excludedDocuments: {
            type: Array,
            required: false,
        },
        linkOnly: {
            type: Boolean,
            default: false,
        },
        limit: {
            type: Number,
            default: Number.MAX_SAFE_INTEGER,
        },
    },
    data() {
        return {
            data: this.item || { name: "", document: null, children: [] },
            froalaStore: useFroalaStore(),
        };
    },
    mounted: function () {
        EventBus.$on("nameInputFocus", (index) => {
            if (this.index === index) {
                this.$refs.nameInput.focus();
            }
        });
    },
    methods: {
        updateName(newName) {
            this.data.name = newName;
        },
        updateSubItemName(newName, index) {
            this.data.children[index].name = newName;
        },
    },
    watch: {
        data: {
            deep: true,
            handler() {
                this.$emit("input", this.data);
            },
        },
    },
};
</script>
